import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { HttpMonitoringApi } from "../../interface/monitoring-api";

type MonitoringTypeFilter = "ALL" | "KEYWORD" | "PRESS_RELEASE";
type DataSourceFilter = "MONITORING" | "PRESS_RELEASE";
type AnalysisMode = "KEYWORD_ARTICLES" | "PRESS_RELEASE_RELATED";
type SortKey = "articleCount" | "negativeCount" | "negativeRate";
type StatTab = "PUBLISHER" | "REPORTER";
type SentimentTableFilter = "ALL" | "NEGATIVE" | "NEUTRAL";
type MinArticleFilter = "ALL" | "2" | "5" | "10";
type HeatmapMetric = "articleCount" | "negativeCount";
type Tone = "red" | "amber" | "blue" | "teal";
type InsightTone = "negative" | "blue" | "teal";
type DrilldownSentiment = "NEGATIVE" | "NEUTRAL";

interface SentimentStatsGroup {
  publisher?: string;
  reporter?: string;
  articleCount: number;
  negativeCount: number;
  neutralCount: number;
  negativeRate: number;
  neutralRate: number;
}

interface KeywordStatsGroup extends SentimentStatsGroup {
  keywordId: string;
  keyword: string;
  publishers: SentimentStatsGroup[];
  reporters: SentimentStatsGroup[];
}

interface SentimentStatsResponse {
  date: string;
  dataSource: DataSourceFilter;
  monitoringType: MonitoringTypeFilter;
  monitoringIds: string[];
  pressReleaseIds: string[];
  deduplicated: boolean;
  totals: SentimentStatsGroup;
  publishers: SentimentStatsGroup[];
  reporters: SentimentStatsGroup[];
  keywords: KeywordStatsGroup[];
}

interface PublisherInsight {
  publisher: SentimentStatsGroup;
  reporters: SentimentStatsGroup[];
}

interface AutoInsight {
  key: string;
  title: string;
  label: string;
  value: string;
  description: string;
  tone: Tone;
  criteria?: DrilldownCriteria;
}

interface DrilldownCriteria {
  title: string;
  caption: string;
  publisher?: string;
  reporter?: string;
  keyword?: string;
  keywordId?: string;
  sentiment?: DrilldownSentiment;
}

interface DrilldownArticle {
  articleId: string;
  title: string;
  publisher: string;
  reporter: string;
  url: string;
  publishedAt: string;
  detectedAt: string;
  sentiment: DrilldownSentiment | "UNKNOWN";
  matchedSearchTerms: string[];
}

const monitoringApi = new HttpMonitoringApi();

const emptyGroup: SentimentStatsGroup = {
  articleCount: 0,
  negativeCount: 0,
  neutralCount: 0,
  negativeRate: 0,
  neutralRate: 0,
};

const emptyStats: SentimentStatsResponse = {
  date: "",
  dataSource: "MONITORING",
  monitoringType: "ALL",
  monitoringIds: [],
  pressReleaseIds: [],
  deduplicated: true,
  totals: emptyGroup,
  publishers: [],
  reporters: [],
  keywords: [],
};

const getAnalysisContext = (mode: AnalysisMode): { dataSource: DataSourceFilter; monitoringType: MonitoringTypeFilter } =>
  mode === "PRESS_RELEASE_RELATED"
    ? { dataSource: "PRESS_RELEASE", monitoringType: "PRESS_RELEASE" }
    : { dataSource: "MONITORING", monitoringType: "KEYWORD" };

const createEmptyStats = (
  dataSource: DataSourceFilter = "MONITORING",
  monitoringType: MonitoringTypeFilter = "KEYWORD",
  date = "",
): SentimentStatsResponse => ({
  ...emptyStats,
  date,
  dataSource,
  monitoringType,
  totals: { ...emptyGroup },
  publishers: [],
  reporters: [],
  keywords: [],
});

const sortOptions: { value: SortKey; label: string }[] = [
  { value: "articleCount", label: "전체 많은 순" },
  { value: "negativeCount", label: "부정 많은 순" },
  { value: "negativeRate", label: "부정률 높은 순" },
];

const tableSentimentFilterOptions: { value: SentimentTableFilter; label: string }[] = [
  { value: "ALL", label: "전체 감성" },
  { value: "NEGATIVE", label: "부정 기사 포함" },
  { value: "NEUTRAL", label: "중립 기사 포함" },
];

const minArticleFilterOptions: { value: MinArticleFilter; label: string }[] = [
  { value: "ALL", label: "전체 기사 수" },
  { value: "2", label: "2건 이상" },
  { value: "5", label: "5건 이상" },
  { value: "10", label: "10건 이상" },
];

const heatmapMetricOptions: { value: HeatmapMetric; label: string }[] = [
  { value: "articleCount", label: "기사량" },
  { value: "negativeCount", label: "부정" },
];

const analysisModeOptions: {
  value: AnalysisMode;
  label: string;
  title: string;
  description: string;
  caption: string;
  tone: "blue" | "teal";
}[] = [
  {
    value: "KEYWORD_ARTICLES",
    label: "키워드 기사",
    title: "키워드 기사 통계",
    description: "키워드 모니터링으로 수집된 기사 기준",
    caption: "언론사·기자별 부정/중립 흐름",
    tone: "blue",
  },
  {
    value: "PRESS_RELEASE_RELATED",
    label: "보도자료 연관 기사",
    title: "보도자료 연관 기사 통계",
    description: "배포 보도자료에 연결된 연관 기사 기준",
    caption: "보도자료별 기사화 언론사·기자 랭킹",
    tone: "teal",
  },
];

const getTodayKst = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

const toNumber = (value: unknown) => {
  const numberValue = Number(value ?? 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const mapGroup = (item: any = {}): SentimentStatsGroup => ({
  publisher: item.publisher,
  reporter: item.reporter,
  articleCount: toNumber(item.articleCount),
  negativeCount: toNumber(item.negativeCount),
  neutralCount: toNumber(item.neutralCount),
  negativeRate: toNumber(item.negativeRate),
  neutralRate: toNumber(item.neutralRate),
});

const mapKeywordGroup = (item: any = {}): KeywordStatsGroup => ({
  ...mapGroup(item),
  keywordId: item.keywordId || "",
  keyword: item.keyword || item.keywordId || "키워드 미확인",
  publishers: Array.isArray(item.publishers) ? item.publishers.map(mapGroup) : [],
  reporters: Array.isArray(item.reporters) ? item.reporters.map(mapGroup) : [],
});

const mapStatsResponse = (item: any = {}, fallbackDataSource: DataSourceFilter = "MONITORING"): SentimentStatsResponse => ({
  date: item.date || "",
  dataSource: item.dataSource === "PRESS_RELEASE" ? "PRESS_RELEASE" : fallbackDataSource,
  monitoringType: item.monitoringType === "KEYWORD" || item.monitoringType === "PRESS_RELEASE" ? item.monitoringType : "ALL",
  monitoringIds: Array.isArray(item.monitoringIds) ? item.monitoringIds : [],
  pressReleaseIds: Array.isArray(item.pressReleaseIds) ? item.pressReleaseIds : [],
  deduplicated: item.deduplicated !== false,
  totals: mapGroup(item.totals),
  publishers: Array.isArray(item.publishers) ? item.publishers.map(mapGroup) : [],
  reporters: Array.isArray(item.reporters) ? item.reporters.map(mapGroup) : [],
  keywords: Array.isArray(item.keywords) ? item.keywords.map(mapKeywordGroup) : [],
});

const mapDrilldownArticle = (item: any = {}): DrilldownArticle => ({
  articleId: item.articleId || "",
  title: item.title || "제목 없음",
  publisher: item.publisher || "언론사 미확인",
  reporter: item.reporter || "기자 미확인",
  url: item.url || "",
  publishedAt: item.publishedAt || "",
  detectedAt: item.detectedAt || "",
  sentiment: item.sentiment === "NEGATIVE" ? "NEGATIVE" : item.sentiment === "POSITIVE" || item.sentiment === "NEUTRAL" ? "NEUTRAL" : "UNKNOWN",
  matchedSearchTerms: Array.isArray(item.matchedSearchTerms) ? item.matchedSearchTerms : [],
});

const getSortValue = (item: SentimentStatsGroup, sortKey: SortKey) => item[sortKey] ?? 0;
const getNegativeCount = (item: SentimentStatsGroup) => item.negativeCount;
const getNegativeRate = (item: SentimentStatsGroup) => item.negativeRate;
const getKeywordKey = (item: KeywordStatsGroup) => item.keywordId || item.keyword;
const getPublisherLabel = (item: SentimentStatsGroup) => item.publisher || "언론사 미확인";
const getReporterLabel = (item: SentimentStatsGroup) => item.reporter || "기자 미확인";
const formatRate = (value: number) => `${value.toFixed(1)}%`;
const getHeatmapMetricValue = (item: SentimentStatsGroup, metric: HeatmapMetric) => item[metric] ?? 0;
const getHeatmapMetricLabel = (metric: HeatmapMetric) =>
  heatmapMetricOptions.find((option) => option.value === metric)?.label || "기사량";

const sortByNegativeSentiment = <T extends SentimentStatsGroup>(items: T[]) =>
  [...items]
    .filter((item) => getNegativeCount(item) > 0)
    .sort((left, right) => {
      const countDiff = getNegativeCount(right) - getNegativeCount(left);
      if (countDiff !== 0) return countDiff;
      const rateDiff = getNegativeRate(right) - getNegativeRate(left);
      if (rateDiff !== 0) return rateDiff;
      const articleDiff = right.articleCount - left.articleCount;
      if (articleDiff !== 0) return articleDiff;
      return `${left.publisher || ""}${left.reporter || ""}`.localeCompare(`${right.publisher || ""}${right.reporter || ""}`);
    });

const sortByArticleCount = <T extends SentimentStatsGroup>(items: T[]) =>
  [...items]
    .filter((item) => item.articleCount > 0)
    .sort((left, right) => {
      const countDiff = right.articleCount - left.articleCount;
      if (countDiff !== 0) return countDiff;
      const negativeDiff = right.negativeCount - left.negativeCount;
      if (negativeDiff !== 0) return negativeDiff;
      return `${left.publisher || ""}${left.reporter || ""}`.localeCompare(`${right.publisher || ""}${right.reporter || ""}`);
    });

const getDominantTone = (item: SentimentStatsGroup): Tone => {
  if (item.negativeCount > 0 && item.negativeCount >= item.neutralCount) return "red";
  return "blue";
};

const buildAutoInsights = (stats: SentimentStatsResponse, dataSource: DataSourceFilter): AutoInsight[] => {
  const insights: AutoInsight[] = [];
  const topNegativePublisher = sortByNegativeSentiment(stats.publishers)[0];
  const topNegativeReporter = sortByNegativeSentiment(stats.reporters)[0];
  const topKeyword = sortByArticleCount(stats.keywords)[0];

  if (dataSource === "PRESS_RELEASE") {
    const topDistributionPublisher = sortByArticleCount(stats.publishers)[0];
    const topDistributionReporter = sortByArticleCount(stats.reporters)[0];
    if (topKeyword) {
      insights.push({
        key: "press-release-response",
        title: "보도자료 기사화",
        label: topKeyword.keyword,
        value: `${topKeyword.articleCount.toLocaleString()}건`,
        description: `부정 ${topKeyword.negativeCount} · 중립 ${topKeyword.neutralCount}`,
        tone: getDominantTone(topKeyword),
        criteria: {
          title: `${topKeyword.keyword} · 연관 기사`,
          caption: "보도자료 기사화 반응",
          keyword: topKeyword.keyword,
          keywordId: topKeyword.keywordId,
        },
      });
    }
    if (topDistributionPublisher) {
      const publisher = getPublisherLabel(topDistributionPublisher);
      insights.push({
        key: "press-release-publisher",
        title: "기사화 집중 언론사",
        label: publisher,
        value: `${topDistributionPublisher.articleCount.toLocaleString()}건`,
        description: `부정 ${topDistributionPublisher.negativeCount} · 중립 ${topDistributionPublisher.neutralCount}`,
        tone: "blue",
        criteria: {
          title: `${publisher} · 보도자료 연관 기사`,
          caption: "기사화 집중 언론사",
          publisher,
        },
      });
    }
    if (topDistributionReporter) {
      const publisher = getPublisherLabel(topDistributionReporter);
      const reporter = getReporterLabel(topDistributionReporter);
      insights.push({
        key: "press-release-reporter",
        title: "기사화 집중 기자",
        label: reporter,
        value: `${topDistributionReporter.articleCount.toLocaleString()}건`,
        description: `${publisher} · 부정 ${topDistributionReporter.negativeCount} · 중립 ${topDistributionReporter.neutralCount}`,
        tone: "teal",
        criteria: {
          title: `${reporter} · 보도자료 연관 기사`,
          caption: publisher,
          publisher,
          reporter,
        },
      });
    }
    return insights.slice(0, 5);
  }

  if (topNegativePublisher) {
    const publisher = getPublisherLabel(topNegativePublisher);
    insights.push({
      key: "risk-publisher",
      title: "주의 필요 언론사",
      label: publisher,
      value: `${topNegativePublisher.negativeCount.toLocaleString()}건`,
      description: `부정률 ${formatRate(topNegativePublisher.negativeRate)} · 전체 ${topNegativePublisher.articleCount.toLocaleString()}건`,
      tone: "red",
      criteria: {
        title: `${publisher} · 부정 기사`,
        caption: "주의 필요 언론사",
        publisher,
        sentiment: "NEGATIVE",
      },
    });
  }

  if (topNegativeReporter) {
    const publisher = getPublisherLabel(topNegativeReporter);
    const reporter = getReporterLabel(topNegativeReporter);
    insights.push({
      key: "risk-reporter",
      title: "주의 필요 기자",
      label: reporter,
      value: `${topNegativeReporter.negativeCount.toLocaleString()}건`,
      description: `${publisher} · 부정률 ${formatRate(topNegativeReporter.negativeRate)}`,
      tone: "amber",
      criteria: {
        title: `${reporter} · 부정 기사`,
        caption: publisher,
        publisher,
        reporter,
        sentiment: "NEGATIVE",
      },
    });
  }

  if (topKeyword) {
    const topKeywordPublisher = sortByArticleCount(topKeyword.publishers)[0];
    const share = topKeywordPublisher && topKeyword.articleCount
      ? Math.round((topKeywordPublisher.articleCount / topKeyword.articleCount) * 100)
      : 0;
    insights.push({
      key: "keyword-leader",
      title: "이슈 주도 키워드",
      label: topKeyword.keyword,
      value: `${topKeyword.articleCount.toLocaleString()}건`,
      description: topKeywordPublisher
        ? `${getPublisherLabel(topKeywordPublisher)} 비중 ${share}% · 부정 ${topKeyword.negativeCount}건`
        : `부정 ${topKeyword.negativeCount} · 중립 ${topKeyword.neutralCount}`,
      tone: "blue",
      criteria: {
        title: `${topKeyword.keyword} · 기사`,
        caption: "이슈 주도 키워드",
        keyword: topKeyword.keyword,
        keywordId: topKeyword.keywordId,
      },
    });
  }

  return insights.slice(0, 5);
};

const PublisherReporterSentimentStatsPage = () => {
  const [date, setDate] = useState(getTodayKst);
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>("KEYWORD_ARTICLES");
  const [sortBy, setSortBy] = useState<SortKey>("articleCount");
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState<StatTab>("PUBLISHER");
  const [selectedKeywordKey, setSelectedKeywordKey] = useState("");
  const [tableSentimentFilter, setTableSentimentFilter] = useState<SentimentTableFilter>("ALL");
  const [minArticleFilter, setMinArticleFilter] = useState<MinArticleFilter>("ALL");
  const [publisherFilter, setPublisherFilter] = useState("ALL");
  const [tablePressReleaseKey, setTablePressReleaseKey] = useState("ALL");
  const [heatmapMetric, setHeatmapMetric] = useState<HeatmapMetric>("articleCount");
  const [stats, setStats] = useState<SentimentStatsResponse>(() => createEmptyStats("MONITORING", "KEYWORD", getTodayKst()));
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<DrilldownCriteria | null>(null);
  const [drilldownArticles, setDrilldownArticles] = useState<DrilldownArticle[]>([]);
  const [drilldownTotalCount, setDrilldownTotalCount] = useState(0);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);
  const statsRequestSeqRef = useRef(0);
  const { dataSource, monitoringType } = getAnalysisContext(analysisMode);
  const activeAnalysisMode = analysisModeOptions.find((option) => option.value === analysisMode) ?? analysisModeOptions[0];

  const loadStats = useCallback(async () => {
    const requestSeq = statsRequestSeqRef.current + 1;
    statsRequestSeqRef.current = requestSeq;
    setLoading(true);
    setLoadError(null);
    try {
      const isPressReleaseSource = dataSource === "PRESS_RELEASE";
      const response = await monitoringApi.post({
        command: isPressReleaseSource ? "get_press_release_publisher_reporter_sentiment_stats" : "get_publisher_reporter_sentiment_stats",
        date,
        monitoringType: isPressReleaseSource ? "PRESS_RELEASE" : monitoringType,
        sortBy,
        publisherLimit: 300,
        reporterLimit: 500,
        keywordLimit: 50,
        keywordPublisherLimit: 5,
        keywordReporterLimit: 5,
        deduplicate: true,
      });
      if (response?.code !== "200") {
        throw new Error(response?.response?.error_msg ?? "통계를 불러오지 못했습니다.");
      }
      if (statsRequestSeqRef.current !== requestSeq) return;
      setStats(mapStatsResponse(response.response, dataSource));
    } catch (error) {
      if (statsRequestSeqRef.current !== requestSeq) return;
      setStats({
        ...createEmptyStats(dataSource, monitoringType, date),
      });
      setLoadError(error instanceof Error ? error.message : "통계를 불러오지 못했습니다.");
    } finally {
      if (statsRequestSeqRef.current === requestSeq) {
        setLoading(false);
      }
    }
  }, [dataSource, date, monitoringType, sortBy]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    setDrilldown(null);
  }, [dataSource, date, monitoringType]);

  useEffect(() => {
    let cancelled = false;
    if (!drilldown) {
      setDrilldownArticles([]);
      setDrilldownTotalCount(0);
      setDrilldownError(null);
      return () => {
        cancelled = true;
      };
    }

    const loadDrilldown = async () => {
      setDrilldownLoading(true);
      setDrilldownError(null);
      try {
        const response = await monitoringApi.post({
          command: dataSource === "PRESS_RELEASE"
            ? "get_press_release_publisher_reporter_sentiment_stat_articles"
            : "get_publisher_reporter_sentiment_stat_articles",
          date,
          monitoringType: dataSource === "PRESS_RELEASE" ? "PRESS_RELEASE" : monitoringType,
          publisher: drilldown.publisher,
          reporter: drilldown.reporter,
          keyword: drilldown.keyword,
          keywordId: drilldown.keywordId,
          sentiment: drilldown.sentiment,
          limit: 50,
          deduplicate: true,
        });
        if (response?.code !== "200") {
          throw new Error(response?.response?.error_msg ?? "기사 목록을 불러오지 못했습니다.");
        }
        if (!cancelled) {
          setDrilldownArticles(Array.isArray(response.response?.articles) ? response.response.articles.map(mapDrilldownArticle) : []);
          setDrilldownTotalCount(toNumber(response.response?.totalCount));
        }
      } catch (error) {
        if (!cancelled) {
          setDrilldownArticles([]);
          setDrilldownTotalCount(0);
          setDrilldownError(error instanceof Error ? error.message : "기사 목록을 불러오지 못했습니다.");
        }
      } finally {
        if (!cancelled) {
          setDrilldownLoading(false);
        }
      }
    };

    loadDrilldown();
    return () => {
      cancelled = true;
    };
  }, [dataSource, date, drilldown, monitoringType]);

  useEffect(() => {
    if (!stats.keywords.length) {
      setSelectedKeywordKey("");
      return;
    }
    const hasSelectedKeyword = stats.keywords.some((item) => getKeywordKey(item) === selectedKeywordKey);
    if (!hasSelectedKeyword) {
      setSelectedKeywordKey(getKeywordKey(stats.keywords[0]));
    }
  }, [selectedKeywordKey, stats.keywords]);

  useEffect(() => {
    if (activeTab !== "REPORTER") {
      setPublisherFilter("ALL");
    }
  }, [activeTab]);

  useEffect(() => {
    if (dataSource !== "PRESS_RELEASE") {
      setTablePressReleaseKey("ALL");
      return;
    }
    if (tablePressReleaseKey === "ALL") return;
    const hasPressRelease = stats.keywords.some((item) => getKeywordKey(item) === tablePressReleaseKey);
    if (!hasPressRelease) {
      setTablePressReleaseKey("ALL");
    }
  }, [dataSource, stats.keywords, tablePressReleaseKey]);

  const selectedTablePressRelease = useMemo(
    () => stats.keywords.find((item) => getKeywordKey(item) === tablePressReleaseKey),
    [stats.keywords, tablePressReleaseKey],
  );

  const tableSourceRows = useMemo(() => {
    if (dataSource === "PRESS_RELEASE" && tablePressReleaseKey !== "ALL" && selectedTablePressRelease) {
      return activeTab === "PUBLISHER" ? selectedTablePressRelease.publishers : selectedTablePressRelease.reporters;
    }
    return activeTab === "PUBLISHER" ? stats.publishers : stats.reporters;
  }, [activeTab, dataSource, selectedTablePressRelease, stats.publishers, stats.reporters, tablePressReleaseKey]);

  const publisherFilterOptions = useMemo(() => {
    const publishers = Array.from(new Set(tableSourceRows.map((item) => item.publisher || "언론사 미확인")));
    return publishers.sort((left, right) => left.localeCompare(right));
  }, [tableSourceRows]);

  useEffect(() => {
    if (publisherFilter !== "ALL" && !publisherFilterOptions.includes(publisherFilter)) {
      setPublisherFilter("ALL");
    }
  }, [publisherFilter, publisherFilterOptions]);

  const rows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const minArticleCount = minArticleFilter === "ALL" ? 0 : Number(minArticleFilter);
    return tableSourceRows
      .filter((item) => {
        if (!query) return true;
        return [item.publisher, item.reporter].some((value) => (value || "").toLowerCase().includes(query));
      })
      .filter((item) => {
        if (tableSentimentFilter === "NEGATIVE") return item.negativeCount > 0;
        if (tableSentimentFilter === "NEUTRAL") return item.neutralCount > 0;
        return true;
      })
      .filter((item) => (minArticleCount ? item.articleCount >= minArticleCount : true))
      .filter((item) => (activeTab === "REPORTER" && publisherFilter !== "ALL" ? (item.publisher || "언론사 미확인") === publisherFilter : true))
      .sort((left, right) => {
        const valueDiff = getSortValue(right, sortBy) - getSortValue(left, sortBy);
        if (valueDiff !== 0) return valueDiff;
        const countDiff = right.articleCount - left.articleCount;
        if (countDiff !== 0) return countDiff;
        return `${left.publisher || ""}${left.reporter || ""}`.localeCompare(`${right.publisher || ""}${right.reporter || ""}`);
      });
  }, [activeTab, minArticleFilter, publisherFilter, searchText, sortBy, tableSentimentFilter, tableSourceRows]);

  const insightData = useMemo(() => {
    const topNegativePublishers = sortByNegativeSentiment(stats.publishers).slice(0, 4);
    const buildPublisherInsights = (publishers: SentimentStatsGroup[]): PublisherInsight[] =>
      publishers.map((publisher) => ({
        publisher,
        reporters: sortByNegativeSentiment(
          stats.reporters.filter((reporter) => reporter.publisher === publisher.publisher),
        ).slice(0, 2),
      }));

    return {
      negativePublishers: buildPublisherInsights(topNegativePublishers),
      negativeReporters: sortByNegativeSentiment(stats.reporters).slice(0, 5),
      distributionPublishers: sortByArticleCount(stats.publishers).slice(0, 5),
      distributionReporters: sortByArticleCount(stats.reporters).slice(0, 5),
    };
  }, [stats.publishers, stats.reporters]);

  const autoInsights = useMemo(() => buildAutoInsights(stats, dataSource), [dataSource, stats]);

  const selectedKeyword = useMemo(
    () => stats.keywords.find((item) => getKeywordKey(item) === selectedKeywordKey) ?? stats.keywords[0],
    [selectedKeywordKey, stats.keywords],
  );
  const sourceLabel = activeAnalysisMode.label;
  const handleDateChange = (value: string) => {
    setDate(value);
    setStats(createEmptyStats(dataSource, monitoringType, value));
    setDrilldown(null);
  };
  const handleAnalysisModeChange = (value: AnalysisMode) => {
    if (value === analysisMode) return;
    const nextContext = getAnalysisContext(value);
    setAnalysisMode(value);
    setStats(createEmptyStats(nextContext.dataSource, nextContext.monitoringType, date));
    setSortBy("articleCount");
    setSearchText("");
    setTableSentimentFilter("ALL");
    setMinArticleFilter("ALL");
    setPublisherFilter("ALL");
    setTablePressReleaseKey("ALL");
  };

  return (
    <Box sx={pageSx}>
      <Stack spacing={1.5}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography sx={pageTitleSx}>언론사·기자 통계</Typography>
            <Typography sx={{ ...pageSubtitleSx, color: loadError ? "#b91c1c" : "#667085" }}>
              {loading ? "통계를 불러오는 중입니다." : loadError ?? `${stats.date || date} 기준 ${sourceLabel} 통계`}
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} variant="outlined" sx={outlineButtonSx} onClick={loadStats} disabled={loading}>
            새로고침
          </Button>
        </Stack>

        <AnalysisModeSelector
          value={analysisMode}
          totals={stats.totals}
          loading={loading}
          onChange={handleAnalysisModeChange}
        />

        <Paper variant="outlined" sx={toolbarSx}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1} alignItems={{ lg: "center" }}>
            <TextField
              type="date"
              label="기준일"
              size="small"
              value={date}
              onChange={(event) => handleDateChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />
            <TextField
              select
              label="정렬"
              size="small"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as SortKey)}
              sx={selectFieldSx}
            >
              {sortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Paper>

        <Box sx={overviewGridSx}>
          <SentimentOverviewChart dataSource={dataSource} totals={stats.totals} loading={loading} />
          <InsightBoard
            dataSource={dataSource}
            negativePublishers={insightData.negativePublishers}
            negativeReporters={insightData.negativeReporters}
            distributionPublishers={insightData.distributionPublishers}
            distributionReporters={insightData.distributionReporters}
            loading={loading}
            onOpenDrilldown={setDrilldown}
          />
        </Box>

        <AutoInsightBoard
          insights={autoInsights}
          loading={loading}
          onOpenDrilldown={setDrilldown}
        />

        <KeywordPublisherHeatmap
          dataSource={dataSource}
          keywords={stats.keywords}
          metric={heatmapMetric}
          onMetricChange={setHeatmapMetric}
          loading={loading}
          onOpenDrilldown={setDrilldown}
        />

        <KeywordStatsBoard
          dataSource={dataSource}
          keywords={stats.keywords}
          selectedKeyword={selectedKeyword}
          selectedKeywordKey={selectedKeywordKey}
          onSelectedKeywordKeyChange={setSelectedKeywordKey}
          onOpenDrilldown={setDrilldown}
          loading={loading}
        />

        <Paper variant="outlined" sx={tableCardSx}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} alignItems={{ md: "center" }} sx={tableHeaderSx}>
            <Tabs
              value={activeTab}
              onChange={(_event, value) => setActiveTab(value)}
              sx={tabsSx}
            >
              <Tab value="PUBLISHER" label="언론사별" />
              <Tab value="REPORTER" label="기자별" />
            </Tabs>
            <Stack direction="row" spacing={0.7} sx={{ flexWrap: "wrap", rowGap: 0.7 }}>
              <Chip size="small" label={`${rows.length}개 그룹`} sx={infoChipSx} />
              <Chip size="small" label={stats.deduplicated ? "중복 기사 제외" : "중복 포함"} sx={infoChipSx} />
              {dataSource === "PRESS_RELEASE" && tablePressReleaseKey !== "ALL" && (
                <Chip size="small" label="선택 보도자료 상위 그룹" sx={infoChipSx} />
              )}
            </Stack>
          </Stack>

          <Stack direction={{ xs: "column", lg: "row" }} spacing={0.8} alignItems={{ lg: "center" }} sx={tableFilterSx}>
            <TextField
              size="small"
              label="테이블 검색"
              placeholder={activeTab === "PUBLISHER" ? "언론사 검색" : "언론사 또는 기자 검색"}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={tableSearchFieldSx}
            />
            <TextField
              select
              label="감성 조건"
              size="small"
              value={tableSentimentFilter}
              onChange={(event) => setTableSentimentFilter(event.target.value as SentimentTableFilter)}
              sx={tableFilterFieldSx}
            >
              {tableSentimentFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="기사 수"
              size="small"
              value={minArticleFilter}
              onChange={(event) => setMinArticleFilter(event.target.value as MinArticleFilter)}
              sx={tableFilterFieldSx}
            >
              {minArticleFilterOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            {activeTab === "REPORTER" && (
              <TextField
                select
                label="언론사"
                size="small"
                value={publisherFilter}
                onChange={(event) => setPublisherFilter(event.target.value)}
                sx={tableFilterFieldSx}
              >
                <MenuItem value="ALL">전체 언론사</MenuItem>
                {publisherFilterOptions.map((publisher) => (
                  <MenuItem key={publisher} value={publisher}>{publisher}</MenuItem>
                ))}
              </TextField>
            )}
            {dataSource === "PRESS_RELEASE" && (
              <TextField
                select
                label="보도자료"
                size="small"
                value={tablePressReleaseKey}
                onChange={(event) => setTablePressReleaseKey(event.target.value)}
                sx={tablePressReleaseFilterFieldSx}
                disabled={!stats.keywords.length}
              >
                <MenuItem value="ALL">전체 보도자료</MenuItem>
                {stats.keywords.map((keyword) => (
                  <MenuItem key={getKeywordKey(keyword)} value={getKeywordKey(keyword)}>
                    {keyword.keyword}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>

          <StatsTable mode={activeTab} rows={rows} loading={loading} />
        </Paper>
      </Stack>
      <DrilldownDrawer
        criteria={drilldown}
        articles={drilldownArticles}
        totalCount={drilldownTotalCount}
        loading={drilldownLoading}
        error={drilldownError}
        onClose={() => setDrilldown(null)}
      />
    </Box>
  );
};

const AnalysisModeSelector = ({
  value,
  totals,
  loading,
  onChange,
}: {
  value: AnalysisMode;
  totals: SentimentStatsGroup;
  loading: boolean;
  onChange: (value: AnalysisMode) => void;
}) => (
  <Paper variant="outlined" sx={analysisSelectorSx}>
    <Stack direction={{ xs: "column", md: "row" }} spacing={0.7} justifyContent="space-between" alignItems={{ md: "center" }} sx={analysisSelectorHeaderSx}>
      <Box>
        <Typography sx={analysisSelectorTitleSx}>분석 대상</Typography>
        <Typography sx={analysisSelectorCaptionSx}>키워드 기사와 보도자료 연관 기사를 별도 기준으로 집계합니다.</Typography>
      </Box>
      <Chip size="small" label="분리 집계" sx={analysisSelectorChipSx} />
    </Stack>
    <Box sx={analysisModeGridSx}>
      {analysisModeOptions.map((option) => {
        const selected = option.value === value;
        const countLabel = selected ? (loading ? "계산 중" : `${totals.articleCount.toLocaleString()}건`) : "전환";
        return (
          <Box
            key={option.value}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            sx={analysisModeCardSx(selected, option.tone)}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onChange(option.value);
              }
            }}
          >
            <Stack direction="row" spacing={0.9} alignItems="flex-start">
              <Box sx={analysisModeIconSx(selected, option.tone)}>
                {option.value === "KEYWORD_ARTICLES" ? <SearchOutlinedIcon sx={{ fontSize: 17 }} /> : <AssessmentOutlinedIcon sx={{ fontSize: 17 }} />}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={0.7} justifyContent="space-between" alignItems="center">
                  <Typography sx={analysisModeTitleSx} noWrap>{option.title}</Typography>
                  <Typography sx={analysisModeCountSx(selected, option.tone)}>{countLabel}</Typography>
                </Stack>
                <Typography sx={analysisModeDescriptionSx}>{option.description}</Typography>
                <Typography sx={analysisModeCaptionSx}>{option.caption}</Typography>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Box>
  </Paper>
);

const InsightBoard = ({
  dataSource,
  negativePublishers,
  negativeReporters,
  distributionPublishers,
  distributionReporters,
  loading,
  onOpenDrilldown,
}: {
  dataSource: DataSourceFilter;
  negativePublishers: PublisherInsight[];
  negativeReporters: SentimentStatsGroup[];
  distributionPublishers: SentimentStatsGroup[];
  distributionReporters: SentimentStatsGroup[];
  loading: boolean;
  onOpenDrilldown: (criteria: DrilldownCriteria) => void;
}) => {
  const isPressReleaseSource = dataSource === "PRESS_RELEASE";
  const headerTitle = isPressReleaseSource ? "보도자료 기사화 랭킹" : "핵심 부정 판정 랭킹";
  const headerCaption = isPressReleaseSource ? "언론사·기자별 보도자료 연관 기사 상위 그룹" : "언론사·기자별 부정 기사량 상위 그룹";
  const gridSx = isPressReleaseSource ? pressReleaseInsightGridSx : insightGridSx;

  return (
    <Paper variant="outlined" sx={insightBoardSx}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={0.7} justifyContent="space-between" alignItems={{ md: "center" }} sx={insightBoardHeaderSx}>
        <Box>
          <Typography sx={insightBoardTitleSx}>{headerTitle}</Typography>
          <Typography sx={insightBoardCaptionSx}>{headerCaption}</Typography>
        </Box>
        <Chip size="small" label="Top 3" sx={insightBoardChipSx} />
      </Stack>
      {isPressReleaseSource ? (
        <Box sx={gridSx}>
          <CompactInsightList
            title="기사화 언론사"
            caption="연관 기사 건수"
            direction="blue"
            rows={distributionPublishers.slice(0, 3).map((item) => {
              const publisher = item.publisher || "언론사 미확인";
              return {
                key: publisher,
                name: publisher,
                meta: `부정 ${item.negativeCount} · 중립 ${item.neutralCount}`,
                count: item.articleCount,
                criteria: {
                  title: `${publisher} · 보도자료 연관 기사`,
                  caption: "보도자료 기사화 언론사 랭킹",
                  publisher,
                },
              };
            })}
            loading={loading}
            onOpenDrilldown={onOpenDrilldown}
          />
          <CompactInsightList
            title="기사화 기자"
            caption="연관 기사 건수"
            direction="teal"
            rows={distributionReporters.slice(0, 3).map((item) => {
              const publisher = item.publisher || "언론사 미확인";
              const reporter = item.reporter || "기자 미확인";
              return {
                key: `${publisher}-${reporter}`,
                name: reporter,
                meta: `${publisher} · 부정 ${item.negativeCount} · 중립 ${item.neutralCount}`,
                count: item.articleCount,
                criteria: {
                  title: `${reporter} · 보도자료 연관 기사`,
                  caption: "보도자료 기사화 기자 랭킹",
                  publisher,
                  reporter,
                },
              };
            })}
            loading={loading}
            onOpenDrilldown={onOpenDrilldown}
          />
        </Box>
      ) : (
        <Box sx={gridSx}>
          <CompactInsightList
            title="부정 언론사"
            caption="부정 건수"
            direction="negative"
            rows={negativePublishers.slice(0, 3).map((item) => ({
              key: item.publisher.publisher || "",
              name: item.publisher.publisher || "언론사 미확인",
              meta: getPublisherInsightMeta(item),
              count: getNegativeCount(item.publisher),
              criteria: {
                title: `${item.publisher.publisher || "언론사 미확인"} · 부정 기사`,
                caption: "부정 기사를 많이 쓴 언론사",
                publisher: item.publisher.publisher || "언론사 미확인",
                sentiment: "NEGATIVE",
              },
            }))}
            loading={loading}
            onOpenDrilldown={onOpenDrilldown}
          />
          <CompactInsightList
            title="부정 기자"
            caption="부정 건수"
            direction="negative"
            rows={negativeReporters.slice(0, 3).map((item) => ({
              key: `${item.publisher || ""}-${item.reporter || ""}`,
              name: item.reporter || "기자 미확인",
              meta: `${item.publisher || "언론사 미확인"} · 전체 ${item.articleCount}건 · 부정률 ${item.negativeRate.toFixed(1)}%`,
              count: getNegativeCount(item),
              criteria: {
                title: `${item.reporter || "기자 미확인"} · 부정 기사`,
                caption: item.publisher || "언론사 미확인",
                publisher: item.publisher || "언론사 미확인",
                reporter: item.reporter || "기자 미확인",
                sentiment: "NEGATIVE",
              },
            }))}
            loading={loading}
            onOpenDrilldown={onOpenDrilldown}
          />
        </Box>
      )}
    </Paper>
  );
};

const getPublisherInsightMeta = (item: PublisherInsight) => {
  const topReporter = item.reporters[0];
  const reporterText = topReporter
    ? `상위 기자 ${topReporter.reporter || "기자 미확인"} ${getNegativeCount(topReporter)}건 · `
    : "";
  return `${reporterText}전체 ${item.publisher.articleCount}건 · 부정률 ${getNegativeRate(item.publisher).toFixed(1)}%`;
};

const CompactInsightList = ({
  title,
  caption,
  direction,
  rows,
  loading,
  onOpenDrilldown,
}: {
  title: string;
  caption: string;
  direction: InsightTone;
  rows: { key: string; name: string; meta: string; count: number; criteria: DrilldownCriteria }[];
  loading: boolean;
  onOpenDrilldown: (criteria: DrilldownCriteria) => void;
}) => (
  <Box sx={compactInsightPanelSx(direction)}>
    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={compactInsightHeaderSx}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={compactInsightTitleSx}>{title}</Typography>
        <Typography sx={compactInsightCaptionSx}>{caption}</Typography>
      </Box>
      <Box sx={compactInsightIconSx(direction)}>
        <AssessmentOutlinedIcon sx={{ fontSize: 14 }} />
      </Box>
    </Stack>
    <Stack spacing={0.45} sx={compactInsightBodySx}>
      {rows.length > 0 ? rows.map((row, index) => (
        <Box
          key={`${row.key}-${index}`}
          role="button"
          tabIndex={0}
          sx={compactInsightRowSx}
          onClick={() => onOpenDrilldown(row.criteria)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              onOpenDrilldown(row.criteria);
            }
          }}
        >
          <Stack direction="row" spacing={0.65} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Typography sx={rankSx(direction)}>{index + 1}</Typography>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={0.65} justifyContent="space-between" alignItems="center">
                <Typography sx={insightTitleSx} noWrap>{row.name}</Typography>
                <Typography sx={insightCountSx(direction)}>{row.count}건</Typography>
              </Stack>
              <Typography sx={insightMetaSx} noWrap>{row.meta}</Typography>
            </Box>
          </Stack>
        </Box>
      )) : (
        <InsightEmpty loading={loading} />
      )}
    </Stack>
  </Box>
);

const InsightEmpty = ({ loading }: { loading: boolean }) => (
  <Box sx={insightEmptySx}>
    <Typography sx={{ color: "#667085", fontSize: 11, fontWeight: 700 }}>
      {loading ? "불러오는 중" : "집계 없음"}
    </Typography>
  </Box>
);

const AutoInsightBoard = ({
  insights,
  loading,
  onOpenDrilldown,
}: {
  insights: AutoInsight[];
  loading: boolean;
  onOpenDrilldown: (criteria: DrilldownCriteria) => void;
}) => (
  <Paper variant="outlined" sx={autoInsightBoardSx}>
    <Stack direction={{ xs: "column", md: "row" }} spacing={0.7} justifyContent="space-between" alignItems={{ md: "center" }} sx={autoInsightHeaderSx}>
      <Box>
        <Typography sx={autoInsightTitleSx}>자동 인사이트</Typography>
        <Typography sx={autoInsightCaptionSx}>선택 기준일 데이터에서 우선 확인할 언론사·기자·이슈</Typography>
      </Box>
      <Chip size="small" label={`${insights.length}개`} sx={autoInsightChipSx} />
    </Stack>
    {insights.length > 0 ? (
      <Box sx={autoInsightGridSx}>
        {insights.map((insight) => {
          const clickable = Boolean(insight.criteria);
          return (
            <Box
              key={insight.key}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              sx={autoInsightCardSx(insight.tone, clickable)}
              onClick={() => insight.criteria && onOpenDrilldown(insight.criteria)}
              onKeyDown={(event) => {
                if (!insight.criteria) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onOpenDrilldown(insight.criteria);
                }
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={autoInsightLabelSx}>{insight.title}</Typography>
                  <Typography sx={autoInsightNameSx} noWrap>{insight.label}</Typography>
                </Box>
                <Typography sx={autoInsightValueSx(insight.tone)}>{insight.value}</Typography>
              </Stack>
              <Typography sx={autoInsightDescriptionSx} noWrap>{insight.description}</Typography>
            </Box>
          );
        })}
      </Box>
    ) : (
      <Box sx={autoInsightEmptySx}>
        <Typography sx={{ color: "#667085", fontSize: 12, fontWeight: 800 }}>
          {loading ? "인사이트를 계산하는 중입니다." : "선택 기준일에 표시할 자동 인사이트가 없습니다."}
        </Typography>
      </Box>
    )}
  </Paper>
);

const KeywordPublisherHeatmap = ({
  dataSource,
  keywords,
  metric,
  onMetricChange,
  loading,
  onOpenDrilldown,
}: {
  dataSource: DataSourceFilter;
  keywords: KeywordStatsGroup[];
  metric: HeatmapMetric;
  onMetricChange: (metric: HeatmapMetric) => void;
  loading: boolean;
  onOpenDrilldown: (criteria: DrilldownCriteria) => void;
}) => {
  const isPressReleaseSource = dataSource === "PRESS_RELEASE";
  const subjectLabel = isPressReleaseSource ? "보도자료" : "키워드";
  const rows = sortByArticleCount(keywords).slice(0, 6);
  const publisherScores = new Map<string, { publisher: string; score: number; articleCount: number }>();
  rows.forEach((keyword) => {
    keyword.publishers.forEach((publisherGroup) => {
      const publisher = getPublisherLabel(publisherGroup);
      const current = publisherScores.get(publisher) ?? { publisher, score: 0, articleCount: 0 };
      publisherScores.set(publisher, {
        publisher,
        score: current.score + getHeatmapMetricValue(publisherGroup, metric),
        articleCount: current.articleCount + publisherGroup.articleCount,
      });
    });
  });
  const publishers = Array.from(publisherScores.values())
    .sort((left, right) => {
      const scoreDiff = right.score - left.score;
      if (scoreDiff !== 0) return scoreDiff;
      return right.articleCount - left.articleCount;
    })
    .slice(0, 6)
    .map((item) => item.publisher);
  const maxValue = Math.max(
    1,
    ...rows.flatMap((keyword) =>
      publishers.map((publisher) => {
        const cell = keyword.publishers.find((item) => getPublisherLabel(item) === publisher);
        return cell ? getHeatmapMetricValue(cell, metric) : 0;
      }),
    ),
  );
  const template = `minmax(138px, 1.05fr) repeat(${Math.max(publishers.length, 1)}, minmax(86px, 1fr))`;

  return (
    <Paper variant="outlined" sx={heatmapBoardSx}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={0.9} justifyContent="space-between" alignItems={{ md: "center" }} sx={heatmapHeaderSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={heatmapTitleSx}>{subjectLabel} × 언론사 히트맵</Typography>
          <Typography sx={heatmapCaptionSx}>{getHeatmapMetricLabel(metric)} 기준으로 이슈별 언론사 반응 강도를 비교합니다.</Typography>
        </Box>
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.7 }}>
          <Chip size="small" label="Top 6×6" sx={heatmapChipSx} />
          <TextField
            select
            size="small"
            label="색상 기준"
            value={metric}
            onChange={(event) => onMetricChange(event.target.value as HeatmapMetric)}
            sx={heatmapMetricFieldSx}
          >
            {heatmapMetricOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>
      {rows.length > 0 && publishers.length > 0 ? (
        <Box sx={heatmapScrollSx}>
          <Box sx={{ minWidth: Math.max(720, 170 + publishers.length * 96) }}>
            <Box sx={heatmapGridSx(template)}>
              <Typography sx={heatmapCornerSx}>{subjectLabel}</Typography>
              {publishers.map((publisher) => (
                <Typography key={publisher} sx={heatmapHeadCellSx} noWrap>{publisher}</Typography>
              ))}
              {rows.map((keyword) => (
                <Fragment key={getKeywordKey(keyword)}>
                  <Box key={`${getKeywordKey(keyword)}-label`} sx={heatmapRowLabelSx}>
                    <Typography sx={heatmapRowTitleSx} noWrap>{keyword.keyword}</Typography>
                    <Typography sx={heatmapRowMetaSx} noWrap>전체 {keyword.articleCount.toLocaleString()}건</Typography>
                  </Box>
                  {publishers.map((publisher) => {
                    const cell = keyword.publishers.find((item) => getPublisherLabel(item) === publisher);
                    const value = cell ? getHeatmapMetricValue(cell, metric) : 0;
                    const intensity = value / maxValue;
                    const criteria: DrilldownCriteria = {
                      title: `${keyword.keyword} · ${publisher} 기사`,
                      caption: `${subjectLabel} × 언론사 히트맵`,
                      publisher,
                      keyword: keyword.keyword,
                      keywordId: keyword.keywordId,
                      sentiment: metric === "negativeCount" ? "NEGATIVE" : undefined,
                    };
                    return (
                      <Box
                        key={`${getKeywordKey(keyword)}-${publisher}`}
                        role="button"
                        tabIndex={0}
                        sx={heatmapCellSx(metric, intensity, value > 0)}
                        onClick={() => value > 0 && onOpenDrilldown(criteria)}
                        onKeyDown={(event) => {
                          if (!value) return;
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            onOpenDrilldown(criteria);
                          }
                        }}
                      >
                        <Typography sx={heatmapCellValueSx}>{value ? value.toLocaleString() : "-"}</Typography>
                      </Box>
                    );
                  })}
                </Fragment>
              ))}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={heatmapEmptySx}>
          <Typography sx={{ color: "#667085", fontSize: 12, fontWeight: 800 }}>
            {loading ? "히트맵을 계산하는 중입니다." : `${subjectLabel}별 언론사 히트맵 데이터가 없습니다.`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const KeywordStatsBoard = ({
  dataSource,
  keywords,
  selectedKeyword,
  selectedKeywordKey,
  onSelectedKeywordKeyChange,
  onOpenDrilldown,
  loading,
}: {
  dataSource: DataSourceFilter;
  keywords: KeywordStatsGroup[];
  selectedKeyword?: KeywordStatsGroup;
  selectedKeywordKey: string;
  onSelectedKeywordKeyChange: (value: string) => void;
  onOpenDrilldown: (criteria: DrilldownCriteria) => void;
  loading: boolean;
}) => {
  const isPressReleaseSource = dataSource === "PRESS_RELEASE";
  const subjectLabel = isPressReleaseSource ? "보도자료" : "키워드";
  return (
    <Paper variant="outlined" sx={keywordBoardSx}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} alignItems={{ md: "center" }} sx={keywordHeaderSx}>
        <Box>
          <Typography sx={keywordTitleSx}>{isPressReleaseSource ? "보도자료별 연관 기사 통계" : "키워드별 기사량 통계"}</Typography>
          <Typography sx={keywordCaptionSx}>
            {selectedKeyword ? `${selectedKeyword.keyword} 기준 상위 언론사·기자` : isPressReleaseSource ? "선택 기준일에 연결된 보도자료 기준" : "선택 기준일에 매칭된 키워드 기준"}
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label={subjectLabel}
          value={selectedKeyword ? getKeywordKey(selectedKeyword) : selectedKeywordKey}
          onChange={(event) => onSelectedKeywordKeyChange(event.target.value)}
          sx={keywordSelectSx}
          disabled={!keywords.length}
        >
          {keywords.map((keyword) => (
            <MenuItem key={getKeywordKey(keyword)} value={getKeywordKey(keyword)}>
              {keyword.keyword}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {keywords.length > 0 ? (
        <Box sx={keywordGridSx}>
          <KeywordRankingPanel
            title={`상위 ${subjectLabel}`}
            keywords={keywords.slice(0, 8)}
            selectedKeywordKey={selectedKeyword ? getKeywordKey(selectedKeyword) : selectedKeywordKey}
            onSelectedKeywordKeyChange={onSelectedKeywordKeyChange}
          />
          <KeywordEntityPanel
            title={`선택 ${subjectLabel} 상위 언론사`}
            caption={selectedKeyword?.keyword || subjectLabel}
            itemLabel={subjectLabel}
            mode="publisher"
            rows={selectedKeyword?.publishers || []}
            keyword={selectedKeyword}
            onOpenDrilldown={onOpenDrilldown}
          />
          <KeywordEntityPanel
            title={`선택 ${subjectLabel} 상위 기자`}
            caption={selectedKeyword?.keyword || subjectLabel}
            itemLabel={subjectLabel}
            mode="reporter"
            rows={selectedKeyword?.reporters || []}
            keyword={selectedKeyword}
            onOpenDrilldown={onOpenDrilldown}
          />
        </Box>
      ) : (
        <Box sx={keywordEmptySx}>
          <Typography sx={{ color: "#667085", fontSize: 12, fontWeight: 700 }}>
            {loading ? `${subjectLabel} 통계를 불러오는 중입니다.` : `선택 기준일에 집계된 ${subjectLabel} 통계가 없습니다.`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const KeywordRankingPanel = ({
  title,
  keywords,
  selectedKeywordKey,
  onSelectedKeywordKeyChange,
}: {
  title: string;
  keywords: KeywordStatsGroup[];
  selectedKeywordKey: string;
  onSelectedKeywordKeyChange: (value: string) => void;
}) => (
  <Box sx={keywordPanelSx}>
    <Typography sx={keywordPanelTitleSx}>{title}</Typography>
    <Stack spacing={0.65}>
      {keywords.map((keyword, index) => {
        const keywordKey = getKeywordKey(keyword);
        const selected = keywordKey === selectedKeywordKey;
        return (
          <Box
            key={keywordKey}
            role="button"
            tabIndex={0}
            sx={keywordRowSx(selected)}
            onClick={() => onSelectedKeywordKeyChange(keywordKey)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                onSelectedKeywordKeyChange(keywordKey);
              }
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography sx={keywordRankSx(selected)}>{index + 1}</Typography>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={keywordNameSx} noWrap>{keyword.keyword}</Typography>
                  <Typography sx={keywordMetaSx} noWrap>
                    부정 {keyword.negativeCount} · 중립 {keyword.neutralCount}
                  </Typography>
                </Box>
              </Stack>
              <Typography sx={keywordCountSx}>{keyword.articleCount}건</Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  </Box>
);

const KeywordEntityPanel = ({
  title,
  caption,
  itemLabel = "키워드",
  mode,
  rows,
  keyword,
  onOpenDrilldown,
}: {
  title: string;
  caption: string;
  itemLabel?: string;
  mode: "publisher" | "reporter";
  rows: SentimentStatsGroup[];
  keyword?: KeywordStatsGroup;
  onOpenDrilldown: (criteria: DrilldownCriteria) => void;
}) => (
  <Box sx={keywordPanelSx}>
    <Typography sx={keywordPanelTitleSx}>{title}</Typography>
    <Typography sx={keywordPanelCaptionSx} noWrap>{caption}</Typography>
    <Stack spacing={0.65} sx={{ mt: 1 }}>
      {rows.length > 0 ? rows.map((row, index) => {
        const publisher = row.publisher || "언론사 미확인";
        const reporter = row.reporter || "기자 미확인";
        const titleText = mode === "publisher" ? `${keyword?.keyword || itemLabel} · ${publisher} 기사` : `${keyword?.keyword || itemLabel} · ${reporter} 기사`;
        const criteria: DrilldownCriteria = {
          title: titleText,
          caption: mode === "publisher" ? `선택 ${itemLabel} 상위 언론사` : publisher,
          publisher,
          reporter: mode === "reporter" ? reporter : undefined,
          keyword: keyword?.keyword,
          keywordId: keyword?.keywordId,
        };
        return (
        <Box
          key={`${row.publisher || ""}-${row.reporter || ""}-${index}`}
          role="button"
          tabIndex={0}
          sx={keywordEntityRowSx}
          onClick={() => onOpenDrilldown(criteria)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              onOpenDrilldown(criteria);
            }
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
            <Stack direction="row" spacing={0.7} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Typography sx={keywordEntityRankSx}>{index + 1}</Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={keywordEntityNameSx} noWrap>
                  {mode === "publisher" ? row.publisher || "언론사 미확인" : row.reporter || "기자 미확인"}
                </Typography>
                <Typography sx={keywordEntityMetaSx} noWrap>
                  {mode === "reporter" ? `${row.publisher || "언론사 미확인"} · ` : ""}부정률 {row.negativeRate.toFixed(1)}%
                </Typography>
              </Box>
            </Stack>
            <Typography sx={keywordEntityCountSx}>{row.articleCount}건</Typography>
          </Stack>
        </Box>
        );
      }) : (
        <Box sx={keywordMiniEmptySx}>
          <Typography sx={{ color: "#94a3b8", fontSize: 11, fontWeight: 700 }}>표시할 집계가 없습니다.</Typography>
        </Box>
      )}
    </Stack>
  </Box>
);

const DrilldownDrawer = ({
  criteria,
  articles,
  totalCount,
  loading,
  error,
  onClose,
}: {
  criteria: DrilldownCriteria | null;
  articles: DrilldownArticle[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) => (
  <Drawer anchor="right" open={Boolean(criteria)} onClose={onClose} PaperProps={{ sx: drawerPaperSx }}>
    <Stack spacing={0} sx={{ height: "100%" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={drawerHeaderSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={drawerTitleSx}>{criteria?.title || "기사 목록"}</Typography>
          <Typography sx={drawerCaptionSx}>{criteria?.caption || "클릭한 조건의 기사"}</Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onClose} sx={drawerCloseButtonSx}>닫기</Button>
      </Stack>
      <Stack direction="row" spacing={0.7} sx={drawerFilterSx}>
        {criteria?.sentiment && <Chip size="small" label={sentimentLabel[criteria.sentiment]} sx={sentimentChipSx(criteria.sentiment)} />}
        {criteria?.publisher && <Chip size="small" label={criteria.publisher} sx={drawerChipSx} />}
        {criteria?.reporter && <Chip size="small" label={criteria.reporter} sx={drawerChipSx} />}
        {criteria?.keyword && <Chip size="small" label={criteria.keyword} sx={drawerChipSx} />}
        <Chip size="small" label={`${totalCount}건`} sx={drawerCountChipSx} />
      </Stack>
      <Box sx={drawerBodySx}>
        {loading ? (
          <Box sx={drawerStateSx}>
            <Typography sx={drawerStateTextSx}>기사 목록을 불러오는 중입니다.</Typography>
          </Box>
        ) : error ? (
          <Box sx={drawerStateSx}>
            <Typography sx={{ ...drawerStateTextSx, color: "#b91c1c" }}>{error}</Typography>
          </Box>
        ) : articles.length > 0 ? articles.map((article) => (
          <Paper key={`${article.articleId}-${article.url}`} variant="outlined" sx={articleCardSx}>
            <Stack spacing={0.45}>
              <Stack direction="row" justifyContent="space-between" spacing={0.8} alignItems="center">
                <Stack direction="row" spacing={0.55} alignItems="center" sx={{ minWidth: 0 }}>
                  <Chip size="small" label={sentimentLabel[article.sentiment] || "미확인"} sx={sentimentChipSx(article.sentiment)} />
                  <Typography sx={articleDateSx} noWrap>{formatArticleDate(article.publishedAt || article.detectedAt)}</Typography>
                </Stack>
                {article.url && (
                  <Button href={article.url} target="_blank" rel="noreferrer" size="small" variant="outlined" sx={articleLinkButtonSx}>
                    원문
                  </Button>
                )}
              </Stack>
              <Typography sx={articleTitleSx}>{article.title}</Typography>
              <Typography sx={articleMetaSx} noWrap>
                {article.publisher} · {article.reporter}
              </Typography>
              {article.matchedSearchTerms.length > 0 && (
                <Stack direction="row" spacing={0.45} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.45 }}>
                  {article.matchedSearchTerms.slice(0, 3).map((term) => (
                    <Chip key={term} size="small" label={term} sx={articleKeywordChipSx} />
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        )) : (
          <Box sx={drawerStateSx}>
            <Typography sx={drawerStateTextSx}>조건에 맞는 기사가 없습니다.</Typography>
          </Box>
        )}
      </Box>
    </Stack>
  </Drawer>
);

const sentimentLabel: Record<string, string> = {
  NEGATIVE: "부정",
  NEUTRAL: "중립",
  UNKNOWN: "미확인",
};

const formatArticleDate = (value: string) => {
  if (!value) return "시각 미확인";
  return value.replace("T", " ").replace("+09:00", "").slice(0, 16);
};

const SentimentOverviewChart = ({ dataSource, totals, loading }: { dataSource: DataSourceFilter; totals: SentimentStatsGroup; loading: boolean }) => {
  const [animated, setAnimated] = useState(false);
  const isPressReleaseSource = dataSource === "PRESS_RELEASE";
  const radius = 39;
  const circumference = 2 * Math.PI * radius;
  const rows = [
    { label: "부정", count: totals.negativeCount, rate: totals.negativeRate, color: "#f04438" },
    { label: "중립", count: totals.neutralCount, rate: totals.neutralRate, color: "#98a2b3" },
  ];
  let offset = 0;
  const segments = rows.map((row, index) => {
    const length = totals.articleCount ? (row.rate / 100) * circumference : 0;
    const segment = { ...row, length, offset, delay: `${index * 120}ms` };
    offset -= length;
    return segment;
  });

  useEffect(() => {
    setAnimated(false);
    const timer = window.setTimeout(() => setAnimated(true), 40);
    return () => window.clearTimeout(timer);
  }, [totals.articleCount, totals.negativeCount, totals.neutralCount]);

  return (
    <Paper variant="outlined" sx={sentimentOverviewSx}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={0.7} justifyContent="space-between" alignItems={{ md: "center" }} sx={sentimentOverviewHeaderSx}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={sentimentOverviewTitleSx}>{isPressReleaseSource ? "연관 기사 감성 분포" : "감성 분포"}</Typography>
          <Typography sx={sentimentOverviewCaptionSx}>
            {loading ? "통계를 불러오는 중입니다." : isPressReleaseSource ? "보도자료 연관 기사 기준 부정/중립 비율" : "키워드 기사 기준 부정/중립 비율"}
          </Typography>
        </Box>
        <Chip size="small" label={`${totals.articleCount.toLocaleString()}건`} sx={sentimentOverviewChipSx} />
      </Stack>
      <Box sx={sentimentOverviewBodySx}>
        <Box sx={sentimentChartAreaSx}>
          <Box sx={donutChartSx}>
            <Box component="svg" viewBox="0 0 100 100" sx={donutSvgSx}>
              <Box
                component="circle"
                cx="50"
                cy="50"
                r={radius}
                sx={donutBaseCircleSx}
              />
              {segments.map((segment) => (
                <Box
                  key={segment.label}
                  component="circle"
                  cx="50"
                  cy="50"
                  r={radius}
                  sx={donutSegmentSx(segment.color, circumference, segment.length, segment.offset, segment.delay, animated)}
                />
              ))}
            </Box>
            <Box sx={donutCenterSx}>
              <Typography sx={donutValueSx}>{totals.articleCount.toLocaleString()}</Typography>
              <Typography sx={donutLabelSx}>전체</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={sentimentLegendGridSx}>
          {rows.map((row) => (
            <Box key={row.label} sx={sentimentLegendItemSx}>
              <Stack direction="row" spacing={0.65} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ ...sentimentLegendDotSx, bgcolor: row.color }} />
                <Typography sx={sentimentLegendLabelSx}>{row.label}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.55} alignItems="baseline" justifyContent="flex-end" sx={{ minWidth: 0, flexWrap: "wrap" }}>
                <Typography sx={sentimentLegendCountSx}>{row.count.toLocaleString()}건</Typography>
                <Typography sx={sentimentLegendRateSx}>{row.rate.toFixed(1)}%</Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

const StatsTable = ({
  mode,
  rows,
  loading,
}: {
  mode: StatTab;
  rows: SentimentStatsGroup[];
  loading: boolean;
}) => {
  const template = mode === "PUBLISHER"
    ? "minmax(160px,1.3fr) 86px 86px 86px 96px minmax(180px,1.2fr)"
    : "minmax(140px,1fr) minmax(140px,1fr) 80px 80px 80px 92px minmax(180px,1.2fr)";

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ minWidth: mode === "PUBLISHER" ? 760 : 900 }}>
        <Box sx={tableHeadSx(template)}>
          <Typography sx={headCellSx}>언론사</Typography>
          {mode === "REPORTER" && <Typography sx={headCellSx}>기자</Typography>}
          <Typography sx={headCellSx} align="right">전체</Typography>
          <Typography sx={headCellSx} align="right">부정</Typography>
          <Typography sx={headCellSx} align="right">중립</Typography>
          <Typography sx={headCellSx} align="right">부정률</Typography>
          <Typography sx={headCellSx}>분포</Typography>
        </Box>
        {rows.length > 0 ? rows.map((row) => (
          <Box key={`${row.publisher || ""}-${row.reporter || ""}`} sx={tableRowSx(template)}>
            <Typography sx={mainCellSx} noWrap>{row.publisher || "언론사 미확인"}</Typography>
            {mode === "REPORTER" && <Typography sx={mainCellSx} noWrap>{row.reporter || "기자 미확인"}</Typography>}
            <NumberCell value={row.articleCount} />
            <NumberCell value={row.negativeCount} tone="red" />
            <NumberCell value={row.neutralCount} tone="gray" />
            <Typography sx={{ ...numberCellSx, color: row.negativeRate > 0 ? "#b42318" : "#475467" }}>
              {row.negativeRate.toFixed(1)}%
            </Typography>
            <SentimentBar row={row} />
          </Box>
        )) : (
          <Box sx={emptySx}>
            <Typography sx={{ color: "#172033", fontSize: 14, fontWeight: 800 }}>
              {loading ? "통계를 불러오는 중입니다." : "표시할 통계가 없습니다."}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const NumberCell = ({ value, tone }: { value: number; tone?: "red" | "gray" }) => (
  <Typography sx={{ ...numberCellSx, color: tone ? numberToneColor[tone] : "#172033" }}>
    {value.toLocaleString()}
  </Typography>
);

const SentimentBar = ({ row }: { row: SentimentStatsGroup }) => {
  const negativeWidth = row.articleCount ? row.negativeRate : 0;
  const neutralWidth = Math.max(0, 100 - negativeWidth);

  return (
    <Stack spacing={0.45}>
      <Box sx={barTrackSx}>
        <Box sx={{ ...barSegmentSx, width: `${negativeWidth}%`, bgcolor: "#f04438" }} />
        <Box sx={{ ...barSegmentSx, width: `${neutralWidth}%`, bgcolor: "#98a2b3" }} />
      </Box>
      <Typography sx={barLabelSx}>
        부정 {row.negativeRate.toFixed(1)} · 중립 {row.neutralRate.toFixed(1)}
      </Typography>
    </Stack>
  );
};

const numberToneColor = {
  red: "#b42318",
  gray: "#475467",
};

const toneBg: Record<Tone, string> = {
  red: "#fee2e2",
  amber: "#fef3c7",
  blue: "#dbeafe",
  teal: "#cffafe",
};

const toneColor: Record<Tone, string> = {
  red: "#b91c1c",
  amber: "#92400e",
  blue: "#1d4ed8",
  teal: "#0e7490",
};

const pageSx = {
  minHeight: "100%",
  bgcolor: "#eef1f6",
  p: { xs: 2, md: 3 },
};

const pageTitleSx = { color: "#172033", fontSize: 24, fontWeight: 900 };
const pageSubtitleSx = { mt: 0.6, color: "#667085", fontSize: 13 };
const panelSx = { overflow: "hidden", borderRadius: "10px", border: "1px solid #e2e8f0", bgcolor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.06)" };
const outlineButtonSx = { borderRadius: "6px", borderColor: "#e2e8f0", color: "#4a5568", fontSize: 12, fontWeight: 500, bgcolor: "#fff", "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" } };
const toolbarSx = { ...panelSx, p: 1.2, overflow: "visible" };
const dateFieldSx = { minWidth: { xs: "100%", lg: 170 }, "& .MuiInputBase-root": { borderRadius: "7px", fontSize: 12, bgcolor: "#fff" }, "& .MuiInputLabel-root": { fontSize: 11 } };
const selectFieldSx = { minWidth: { xs: "100%", lg: 160 }, "& .MuiInputBase-root": { borderRadius: "7px", fontSize: 12, bgcolor: "#fff" }, "& .MuiInputLabel-root": { fontSize: 11 } };
const analysisSelectorSx = { ...panelSx, overflow: "hidden" };
const analysisSelectorHeaderSx = { px: 1.5, py: 1.05, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" };
const analysisSelectorTitleSx = { color: "#1a2130", fontSize: 13, fontWeight: 800, lineHeight: 1.3 };
const analysisSelectorCaptionSx = { mt: 0.2, color: "#98a2b3", fontSize: 11, lineHeight: 1.35 };
const analysisSelectorChipSx = { height: 22, borderRadius: "20px", bgcolor: "#f1f5f9", color: "#475467", fontSize: 10, fontWeight: 800, "& .MuiChip-label": { px: 0.85 } };
const analysisModeGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 0.85, p: 1 };
const analysisModeAccent = (tone: "blue" | "teal") => (tone === "blue" ? "#2563eb" : "#0d9488");
const analysisModeSoftBg = (tone: "blue" | "teal") => (tone === "blue" ? "#eff6ff" : "#f0fdfa");
const analysisModeCardSx = (selected: boolean, tone: "blue" | "teal") => ({
  border: `1px solid ${selected ? analysisModeAccent(tone) : "#e2e8f0"}`,
  borderRadius: "8px",
  bgcolor: selected ? analysisModeSoftBg(tone) : "#fff",
  px: 1.15,
  py: 1.05,
  minWidth: 0,
  cursor: "pointer",
  outline: "none",
  transition: "border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
  "&:hover": { borderColor: selected ? analysisModeAccent(tone) : "#cbd5e1", bgcolor: selected ? analysisModeSoftBg(tone) : "#f8fafc", boxShadow: "0 4px 12px rgba(15,23,42,.06)" },
  "&:focus-visible": { boxShadow: `0 0 0 3px ${tone === "blue" ? "rgba(37,99,235,.16)" : "rgba(13,148,136,.18)"}` },
});
const analysisModeIconSx = (selected: boolean, tone: "blue" | "teal") => ({
  width: 34,
  height: 34,
  borderRadius: "8px",
  bgcolor: selected ? analysisModeAccent(tone) : analysisModeSoftBg(tone),
  color: selected ? "#fff" : analysisModeAccent(tone),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});
const analysisModeTitleSx = { color: "#172033", fontSize: 13, fontWeight: 900, lineHeight: 1.3 };
const analysisModeDescriptionSx = { mt: 0.35, color: "#475467", fontSize: 11.5, fontWeight: 700, lineHeight: 1.35 };
const analysisModeCaptionSx = { mt: 0.25, color: "#98a2b3", fontSize: 10.5, fontWeight: 600, lineHeight: 1.35 };
const analysisModeCountSx = (selected: boolean, tone: "blue" | "teal") => ({
  color: selected ? analysisModeAccent(tone) : "#667085",
  bgcolor: selected ? "#fff" : "#f1f5f9",
  borderRadius: "20px",
  px: 0.8,
  py: 0.18,
  fontSize: 10.5,
  fontWeight: 900,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
  flexShrink: 0,
});
const overviewGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", lg: "minmax(280px, .82fr) minmax(0, 2fr)" },
  gap: 1,
  alignItems: "stretch",
};
const insightGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 0.8, p: 1 };
const pressReleaseInsightGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 0.8, p: 1 };

const sentimentOverviewSx = { ...panelSx, height: "100%", display: "flex", flexDirection: "column" };
const sentimentOverviewHeaderSx = { px: 1.5, py: 1.05, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" };
const sentimentOverviewBodySx = { p: 1.25, minHeight: 0, flex: 1, display: "flex", flexDirection: "column", gap: 1 };
const sentimentChartAreaSx = {
  flex: 1,
  minHeight: { xs: 178, lg: 0 },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  py: 0.6,
};
const sentimentOverviewChipSx = { height: 22, borderRadius: "20px", bgcolor: "#f1f5f9", color: "#475467", fontSize: 10, fontWeight: 800, "& .MuiChip-label": { px: 0.85 } };
const donutChartSx = {
  width: { xs: 150, lg: 176 },
  height: { xs: 150, lg: 176 },
  borderRadius: "50%",
  bgcolor: "#fff",
  position: "relative",
  flexShrink: 0,
  filter: "drop-shadow(0 6px 14px rgba(15,23,42,.10))",
};
const donutSvgSx = { width: "100%", height: "100%", transform: "rotate(-90deg)", overflow: "visible" };
const donutBaseCircleSx = {
  fill: "none",
  stroke: "#eef2f7",
  strokeWidth: 12,
};
const donutSegmentSx = (
  color: string,
  circumference: number,
  length: number,
  offset: number,
  delay: string,
  animated: boolean,
) => ({
  fill: "none",
  stroke: color,
  strokeWidth: 12,
  strokeLinecap: "round",
  strokeDasharray: `${animated ? length : 0} ${circumference}`,
  strokeDashoffset: offset,
  transition: "stroke-dasharray .72s ease-out",
  transitionDelay: delay,
});
const donutCenterSx = {
  position: "absolute",
  inset: "24%",
  borderRadius: "50%",
  bgcolor: "#fff",
  border: "1px solid #e2e8f0",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
};
const donutValueSx = { color: "#172033", fontSize: { xs: 19, lg: 22 }, fontWeight: 900, lineHeight: 1.1 };
const donutLabelSx = { mt: 0.15, color: "#98a2b3", fontSize: 11, fontWeight: 700, lineHeight: 1 };
const sentimentOverviewTitleSx = { color: "#1a2130", fontSize: 13, fontWeight: 800, lineHeight: 1.3 };
const sentimentOverviewCaptionSx = { mt: 0.2, color: "#98a2b3", fontSize: 11, lineHeight: 1.35 };
const sentimentLegendGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "1fr" }, gap: 0.65, width: "100%", mt: "auto" };
const sentimentLegendItemSx = {
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  bgcolor: "#f8fafc",
  px: 1,
  py: 0.65,
  minWidth: 0,
  minHeight: 42,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 0.8,
};
const sentimentLegendDotSx = { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 };
const sentimentLegendLabelSx = { color: "#667085", fontSize: 11, fontWeight: 700 };
const sentimentLegendCountSx = { color: "#172033", fontSize: 13, fontWeight: 900, lineHeight: 1.25 };
const sentimentLegendRateSx = { color: "#98a2b3", fontSize: 10.5, fontWeight: 700 };

const insightBoardSx = { ...panelSx, height: "100%" };
const insightBoardHeaderSx = { px: 1.5, py: 1.05, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" };
const insightBoardTitleSx = { color: "#1a2130", fontSize: 13, fontWeight: 800, lineHeight: 1.3 };
const insightBoardCaptionSx = { mt: 0.2, color: "#98a2b3", fontSize: 11, lineHeight: 1.35 };
const insightBoardChipSx = { height: 22, borderRadius: "20px", bgcolor: "#f1f5f9", color: "#475467", fontSize: 10, fontWeight: 800, "& .MuiChip-label": { px: 0.85 } };
const compactInsightPanelSx = (direction: InsightTone) => ({
  border: `1px solid ${direction === "negative" ? "#fecaca" : direction === "blue" ? "#bfdbfe" : "#99f6e4"}`,
  borderRadius: "8px",
  bgcolor: direction === "negative" ? "#fffafa" : direction === "blue" ? "#f8fbff" : "#f7fffd",
  minWidth: 0,
  overflow: "hidden",
});
const compactInsightHeaderSx = { px: 1.1, py: 0.85, borderBottom: "1px solid rgba(226,232,240,.85)" };
const compactInsightTitleSx = { color: "#1a2130", fontSize: 12, fontWeight: 800, lineHeight: 1.25 };
const compactInsightCaptionSx = { mt: 0.15, color: "#94a3b8", fontSize: 10.5, lineHeight: 1.3 };
const compactInsightIconSx = (direction: InsightTone) => ({
  width: 24,
  height: 24,
  borderRadius: "7px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: direction === "negative" ? toneBg.red : direction === "blue" ? toneBg.blue : toneBg.teal,
  color: direction === "negative" ? toneColor.red : direction === "blue" ? toneColor.blue : toneColor.teal,
  flexShrink: 0,
});
const compactInsightBodySx = { p: 0.75 };
const compactInsightRowSx = {
  border: "1px solid rgba(226,232,240,.88)",
  bgcolor: "rgba(255,255,255,.72)",
  px: 0.75,
  py: 0.65,
  borderRadius: "7px",
  cursor: "pointer",
  outline: "none",
  transition: "border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
  "&:hover": { borderColor: "#cbd5e1", bgcolor: "rgba(255,255,255,.92)", boxShadow: "0 4px 12px rgba(15,23,42,.06)" },
  "&:focus-visible": { boxShadow: "0 0 0 3px rgba(8,145,178,.18)" },
};
const rankSx = (direction: InsightTone) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 18,
  height: 18,
  borderRadius: "20px",
  bgcolor: direction === "negative" ? "#fee2e2" : direction === "blue" ? "#dbeafe" : "#ccfbf1",
  color: direction === "negative" ? "#b91c1c" : direction === "blue" ? "#1d4ed8" : "#0f766e",
  fontSize: 9.5,
  fontWeight: 800,
  flexShrink: 0,
});
const insightTitleSx = { color: "#1a2130", fontSize: 11.5, fontWeight: 800, lineHeight: 1.25, minWidth: 0 };
const insightMetaSx = { mt: 0.2, color: "#94a3b8", fontSize: 10.5, lineHeight: 1.3 };
const insightCountSx = (direction: InsightTone) => ({
  display: "inline-flex",
  alignItems: "center",
  color: direction === "negative" ? "#dc2626" : direction === "blue" ? "#2563eb" : "#0d9488",
  fontSize: 10.5,
  fontWeight: 800,
  whiteSpace: "nowrap",
  borderRadius: "20px",
  px: 0.75,
  py: 0.18,
  bgcolor: direction === "negative" ? "#fee2e2" : direction === "blue" ? "#dbeafe" : "#ccfbf1",
});
const insightEmptySx = { border: "1px dashed #cbd5e1", borderRadius: "8px", bgcolor: "#f8fafc", px: 1, py: 1.2, textAlign: "center" };

const autoInsightBoardSx = { ...panelSx, overflow: "hidden" };
const autoInsightHeaderSx = { px: 1.5, py: 1.05, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" };
const autoInsightTitleSx = { color: "#1a2130", fontSize: 13, fontWeight: 800, lineHeight: 1.3 };
const autoInsightCaptionSx = { mt: 0.2, color: "#98a2b3", fontSize: 11, lineHeight: 1.35 };
const autoInsightChipSx = { height: 22, borderRadius: "20px", bgcolor: "#f1f5f9", color: "#475467", fontSize: 10, fontWeight: 800, "& .MuiChip-label": { px: 0.85 } };
const autoInsightGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(auto-fit, minmax(180px, 1fr))" }, gap: 0.8, p: 1 };
const autoInsightCardSx = (tone: Tone, clickable: boolean) => ({
  border: `1px solid ${toneBg[tone]}`,
  borderRadius: "8px",
  bgcolor: tone === "red" ? "#fff7f7" : tone === "amber" ? "#fffbeb" : tone === "teal" ? "#f0fdfa" : "#f8fbff",
  minWidth: 0,
  px: 1.1,
  py: 0.95,
  cursor: clickable ? "pointer" : "default",
  outline: "none",
  transition: "border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
  "&:hover": clickable ? { borderColor: toneColor[tone], boxShadow: "0 4px 12px rgba(15,23,42,.06)" } : undefined,
  "&:focus-visible": clickable ? { boxShadow: "0 0 0 3px rgba(8,145,178,.18)" } : undefined,
});
const autoInsightLabelSx = { color: "#667085", fontSize: 10.5, fontWeight: 800, lineHeight: 1.3 };
const autoInsightNameSx = { mt: 0.25, color: "#1a2130", fontSize: 12.5, fontWeight: 900, lineHeight: 1.35 };
const autoInsightValueSx = (tone: Tone) => ({
  color: toneColor[tone],
  bgcolor: toneBg[tone],
  borderRadius: "20px",
  px: 0.75,
  py: 0.2,
  fontSize: 11,
  fontWeight: 900,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
  flexShrink: 0,
});
const autoInsightDescriptionSx = { mt: 0.7, color: "#667085", fontSize: 10.8, fontWeight: 600, lineHeight: 1.35 };
const autoInsightEmptySx = { px: 1.5, py: 1.8, textAlign: "center", bgcolor: "#fff" };

const heatmapBoardSx = { ...panelSx, overflow: "hidden" };
const heatmapHeaderSx = { px: 1.5, py: 1.05, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff" };
const heatmapTitleSx = { color: "#1a2130", fontSize: 13, fontWeight: 800, lineHeight: 1.3 };
const heatmapCaptionSx = { mt: 0.2, color: "#98a2b3", fontSize: 11, lineHeight: 1.35 };
const heatmapChipSx = { height: 22, borderRadius: "20px", bgcolor: "#e0f2fe", color: "#0369a1", fontSize: 10, fontWeight: 800, "& .MuiChip-label": { px: 0.85 } };
const heatmapMetricFieldSx = {
  minWidth: 120,
  "& .MuiInputBase-root": { borderRadius: "7px", fontSize: 12, bgcolor: "#fff" },
  "& .MuiInputLabel-root": { fontSize: 11 },
};
const heatmapScrollSx = { overflowX: "auto", bgcolor: "#fff" };
const heatmapGridSx = (template: string) => ({
  display: "grid",
  gridTemplateColumns: template,
  gap: 0.45,
  p: 1,
});
const heatmapCornerSx = { color: "#667085", fontSize: 10.5, fontWeight: 800, px: 0.8, py: 0.6 };
const heatmapHeadCellSx = {
  color: "#475467",
  fontSize: 10.5,
  fontWeight: 800,
  textAlign: "center",
  borderRadius: "7px",
  bgcolor: "#f8fafc",
  px: 0.75,
  py: 0.6,
};
const heatmapRowLabelSx = {
  border: "1px solid #e2e8f0",
  borderRadius: "7px",
  bgcolor: "#f8fafc",
  px: 0.85,
  py: 0.65,
  minWidth: 0,
};
const heatmapRowTitleSx = { color: "#1a2130", fontSize: 11.5, fontWeight: 900, lineHeight: 1.25 };
const heatmapRowMetaSx = { mt: 0.2, color: "#98a2b3", fontSize: 10, fontWeight: 700, lineHeight: 1.25 };
const getHeatmapCellColor = (metric: HeatmapMetric, intensity: number) => {
  if (intensity <= 0) return "#f8fafc";
  const alpha = Math.min(0.76, 0.12 + intensity * 0.58);
  if (metric === "negativeCount") return `rgba(240, 68, 56, ${alpha})`;
  return `rgba(14, 165, 233, ${alpha})`;
};
const heatmapCellSx = (metric: HeatmapMetric, intensity: number, clickable: boolean) => ({
  minHeight: 42,
  borderRadius: "7px",
  border: "1px solid rgba(226,232,240,.9)",
  bgcolor: getHeatmapCellColor(metric, intensity),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: clickable ? "pointer" : "default",
  outline: "none",
  transition: "transform .12s ease, box-shadow .12s ease, border-color .12s ease",
  "&:hover": clickable ? { transform: "translateY(-1px)", borderColor: "#94a3b8", boxShadow: "0 4px 10px rgba(15,23,42,.08)" } : undefined,
  "&:focus-visible": clickable ? { boxShadow: "0 0 0 3px rgba(8,145,178,.18)" } : undefined,
});
const heatmapCellValueSx = { color: "#172033", fontSize: 12, fontWeight: 900, lineHeight: 1.2 };
const heatmapEmptySx = { px: 1.5, py: 1.8, textAlign: "center", bgcolor: "#fff" };

const keywordBoardSx = { ...panelSx, overflow: "visible" };
const keywordHeaderSx = { px: 1.5, py: 1.25, borderBottom: "1px solid #e2e8f0" };
const keywordTitleSx = { color: "#1a2130", fontSize: 14, fontWeight: 800, lineHeight: 1.35 };
const keywordCaptionSx = { mt: 0.2, color: "#98a2b3", fontSize: 11, lineHeight: 1.4 };
const keywordSelectSx = { minWidth: { xs: "100%", md: 220 }, "& .MuiInputBase-root": { borderRadius: "7px", fontSize: 12, bgcolor: "#fff" }, "& .MuiInputLabel-root": { fontSize: 11 } };
const keywordGridSx = { display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.05fr repeat(2, minmax(0, 1fr))" }, gap: 1, p: 1 };
const keywordPanelSx = { border: "1px solid #e2e8f0", borderRadius: "8px", bgcolor: "#fff", p: 1.1, minWidth: 0 };
const keywordPanelTitleSx = { color: "#1a2130", fontSize: 12, fontWeight: 800, lineHeight: 1.35 };
const keywordPanelCaptionSx = { mt: 0.25, color: "#98a2b3", fontSize: 11, lineHeight: 1.4 };
const keywordRowSx = (selected: boolean) => ({
  border: `1px solid ${selected ? "#0891b2" : "#e2e8f0"}`,
  borderRadius: "8px",
  bgcolor: selected ? "#eff6ff" : "#f8fafc",
  px: 1,
  py: 0.85,
  cursor: "pointer",
  outline: "none",
  transition: "border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
  "&:hover": { borderColor: selected ? "#0e7490" : "#cbd5e1", bgcolor: selected ? "#e0f2fe" : "#f1f5f9" },
  "&:focus-visible": { boxShadow: "0 0 0 3px rgba(8,145,178,.18)" },
});
const keywordRankSx = (selected: boolean) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "20px",
  bgcolor: selected ? "#bae6fd" : "#e2e8f0",
  color: selected ? "#0369a1" : "#475467",
  fontSize: 10,
  fontWeight: 800,
  flexShrink: 0,
});
const keywordNameSx = { color: "#1a2130", fontSize: 12, fontWeight: 800, lineHeight: 1.35 };
const keywordMetaSx = { mt: 0.2, color: "#94a3b8", fontSize: 10.5, lineHeight: 1.35 };
const keywordCountSx = { color: "#0369a1", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap", borderRadius: "20px", bgcolor: "#e0f2fe", px: 1, py: 0.25 };
const keywordEntityRowSx = {
  border: "1px solid #e2e8f0",
  borderRadius: "8px",
  bgcolor: "#f8fafc",
  px: 1,
  py: 0.85,
  cursor: "pointer",
  outline: "none",
  transition: "border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
  "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f1f5f9", boxShadow: "0 4px 12px rgba(15,23,42,.06)" },
  "&:focus-visible": { boxShadow: "0 0 0 3px rgba(8,145,178,.18)" },
};
const keywordEntityRankSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "20px",
  bgcolor: "#e0f2fe",
  color: "#0369a1",
  fontSize: 10,
  fontWeight: 800,
  flexShrink: 0,
};
const keywordEntityNameSx = { color: "#1a2130", fontSize: 12, fontWeight: 800, lineHeight: 1.35 };
const keywordEntityMetaSx = { mt: 0.2, color: "#94a3b8", fontSize: 10.5, lineHeight: 1.35 };
const keywordEntityCountSx = { color: "#172033", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" };
const keywordEmptySx = { px: 1.5, py: 2.5, textAlign: "center", bgcolor: "#fff" };
const keywordMiniEmptySx = { border: "1px dashed #cbd5e1", borderRadius: "8px", bgcolor: "#f8fafc", px: 1, py: 1.5, textAlign: "center" };

const tableCardSx = { ...panelSx };
const tableHeaderSx = { px: 2, pt: 1.35, pb: 1.1, borderBottom: "1px solid #e2e8f0" };
const tableFilterSx = { px: 1.5, py: 1, borderBottom: "1px solid #e2e8f0", bgcolor: "#fbfcfe" };
const tableSearchFieldSx = {
  flex: 1,
  minWidth: { xs: "100%", lg: 240 },
  "& .MuiInputBase-root": { borderRadius: "7px", fontSize: 12, bgcolor: "#fff" },
  "& .MuiInputLabel-root": { fontSize: 11 },
};
const tableFilterFieldSx = {
  minWidth: { xs: "100%", lg: 138 },
  "& .MuiInputBase-root": { borderRadius: "7px", fontSize: 12, bgcolor: "#fff" },
  "& .MuiInputLabel-root": { fontSize: 11 },
};
const tablePressReleaseFilterFieldSx = {
  minWidth: { xs: "100%", lg: 240 },
  "& .MuiInputBase-root": { borderRadius: "7px", fontSize: 12, bgcolor: "#fff" },
  "& .MuiInputLabel-root": { fontSize: 11 },
};
const tabsSx = {
  minHeight: 32,
  "& .MuiTab-root": { minHeight: 32, px: 1.3, fontSize: 12, fontWeight: 700, color: "#667085" },
  "& .Mui-selected": { color: "#0891b2" },
  "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0", bgcolor: "#0891b2" },
};
const infoChipSx = { height: 22, borderRadius: "20px", bgcolor: "#e0f2fe", color: "#0369a1", fontSize: 10, fontWeight: 600, "& .MuiChip-label": { px: 0.85 } };
const tableHeadSx = (template: string) => ({
  display: "grid",
  gridTemplateColumns: template,
  gap: 1,
  alignItems: "center",
  px: 1.5,
  py: 0.85,
  bgcolor: "#f8fafc",
  borderBottom: "1px solid #e2e8f0",
});
const tableRowSx = (template: string) => ({
  display: "grid",
  gridTemplateColumns: template,
  gap: 1,
  alignItems: "center",
  px: 1.5,
  py: 1,
  borderBottom: "1px solid #e2e8f0",
  bgcolor: "#fff",
  "&:hover": { bgcolor: "#fbfcfe" },
});
const headCellSx = { color: "#667085", fontSize: 11, fontWeight: 600 };
const mainCellSx = { color: "#1a2130", fontSize: 12, fontWeight: 700 };
const numberCellSx = { color: "#172033", fontSize: 12, fontWeight: 700, textAlign: "right" };
const barTrackSx = { width: "100%", height: 8, borderRadius: "8px", overflow: "hidden", display: "flex", bgcolor: "#f2f4f7" };
const barSegmentSx = { height: "100%", minWidth: 0 };
const barLabelSx = { color: "#94a3b8", fontSize: 11, lineHeight: 1.4 };
const emptySx = { p: 3, textAlign: "center", bgcolor: "#fff" };

const drawerPaperSx = { width: { xs: "100%", sm: 420 }, bgcolor: "#eef1f6", borderLeft: "1px solid #e2e8f0" };
const drawerHeaderSx = { px: 1.5, py: 1.1, bgcolor: "#fff", borderBottom: "1px solid #e2e8f0" };
const drawerTitleSx = { color: "#172033", fontSize: 14, fontWeight: 900, lineHeight: 1.3 };
const drawerCaptionSx = { mt: 0.2, color: "#667085", fontSize: 11, lineHeight: 1.35 };
const drawerCloseButtonSx = { ...outlineButtonSx, minWidth: 44, px: 0.9, py: 0.25, borderRadius: "6px", fontSize: 11, lineHeight: 1.4 };
const drawerFilterSx = { px: 1, py: 0.75, flexWrap: "wrap", rowGap: 0.55, bgcolor: "#fff", borderBottom: "1px solid #e2e8f0" };
const drawerChipSx = { height: 20, borderRadius: "20px", bgcolor: "#fff", color: "#475467", border: "1px solid #e2e8f0", fontSize: 10, fontWeight: 700, "& .MuiChip-label": { px: 0.75 } };
const drawerCountChipSx = { ...drawerChipSx, bgcolor: "#e0f2fe", color: "#0369a1", borderColor: "#bae6fd" };
const drawerBodySx = { px: 1, py: 1, overflowY: "auto", flex: 1 };
const drawerStateSx = { border: "1px dashed #cbd5e1", borderRadius: "8px", bgcolor: "#fff", px: 1.25, py: 2.2, textAlign: "center" };
const drawerStateTextSx = { color: "#667085", fontSize: 12, fontWeight: 800 };
const articleCardSx = { mb: 0.7, borderRadius: "8px", borderColor: "#e2e8f0", bgcolor: "#f8fafc", px: 1.15, py: 0.85 };
const articleTitleSx = { color: "#1a2130", fontSize: 12, fontWeight: 500, lineHeight: 1.5 };
const articleMetaSx = { color: "#94a3b8", fontSize: 10.5, fontWeight: 500, lineHeight: 1.35, minWidth: 0 };
const articleDateSx = { color: "#98a2b3", fontSize: 10.5, fontWeight: 600, lineHeight: "20px" };
const articleKeywordChipSx = { height: 18, borderRadius: "20px", bgcolor: "#e0f2fe", color: "#0369a1", fontSize: 9.5, fontWeight: 600, "& .MuiChip-label": { px: 0.65 } };
const articleLinkButtonSx = { ...outlineButtonSx, minWidth: 0, px: 0.75, py: 0.15, fontSize: 10.5, lineHeight: 1.35, flexShrink: 0 };
const sentimentChipSx = (sentiment: string) => ({
  height: 20,
  borderRadius: "20px",
  bgcolor: sentiment === "NEGATIVE" ? "#fee2e2" : "#f1f5f9",
  color: sentiment === "NEGATIVE" ? "#b91c1c" : "#475467",
  fontSize: 10,
  fontWeight: 800,
  "& .MuiChip-label": { px: 0.75 },
});

export default PublisherReporterSentimentStatsPage;
